import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  MessageSquare,
  Camera,
  Calendar,
  Search,
  FileCheck,
  Scale,
  ShieldCheck,
} from "lucide-react";
import { Phone, FileText, Users, Briefcase } from "lucide-react";
import { STEPS } from "@/mock";

gsap.registerPlugin(ScrollTrigger);

const ICONS = { MessageSquare, Camera, Calendar, Search, FileCheck, Scale };

export default function Steps() {
  const sectionRef = useRef(null);
  const progressRef = useRef(null);
  const cursorRef = useRef(null);
  const [cursorIcon, setCursorIcon] = useState("");

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Progress bar animation
      if (progressRef.current) {
        gsap.fromTo(
          progressRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            transformOrigin: "left center",
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 70%",
              end: "bottom 30%",
              scrub: 0.3,
            },
          }
        );
      }

      // Simple card animations
      gsap.utils.toArray("[data-prof-step]").forEach((el, i) => {
        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            once: true
          },
          y: 30,
          opacity: 0,
          duration: 0.6,
          ease: "power2.out",
          delay: i * 0.1,
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Simple cursor follower without throttling
  useEffect(() => {
    const el = cursorRef.current;
    const host = sectionRef.current;
    if (!el || !host) return;

    const onMove = (e) => {
      const rect = host.getBoundingClientRect();
      gsap.set(el, {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    host.addEventListener("mousemove", onMove);
    return () => host.removeEventListener("mousemove", onMove);
  }, []);

  const showCursor = () => {
    if (!cursorRef.current) return;
    gsap.to(cursorRef.current, { 
      autoAlpha: 1, 
      scale: 1, 
      duration: 0.2, 
      ease: "power2.out" 
    });
  };

  const hideCursor = () => {
    if (!cursorRef.current) return;
    gsap.to(cursorRef.current, { 
      autoAlpha: 0, 
      scale: 0.8, 
      duration: 0.15, 
      ease: "power2.in" 
    });
  };

  const STEP_KEYS = ["kontakt", "unfall", "termin", "begutachtung", "gutachten", "anwalt"];
  const CURSOR_ICON_MAP = {
    kontakt: <Phone className="w-4 h-4" />,
    unfall: <FileText className="w-4 h-4" />,
    termin: <Calendar className="w-4 h-4" />,
    begutachtung: <Camera className="w-4 h-4" />,
    gutachten: <Users className="w-4 h-4" />,
    anwalt: <Briefcase className="w-4 h-4" />,
  };

  return (
    <section
      id="ablauf"
      ref={sectionRef}
      className="relative bg-[#f5f7f8] text-neutral-900 border-t border-black/10"
    >
      <div className="mx-auto max-w-7xl px-6 py-16">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Badge className="bg-black/5 text-neutral-900 hover:bg-black/10 transition-colors">
            Schritt für Schritt
          </Badge>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            So läuft dein Gutachten ab
          </h2>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="h-1 w-full rounded bg-black/10 overflow-hidden">
            <div 
              ref={progressRef} 
              className="h-1 w-full bg-[var(--brand-red)] origin-left"
              style={{ transform: 'scaleX(0)' }}
            />
          </div>
        </div>

        {/* Steps Grid */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {STEPS.map((step, idx) => {
            const Icon = ICONS[step.icon] || ShieldCheck;
            const stepKey = STEP_KEYS[idx] || "kontakt";
            
            return (
              <Card
                key={step.id}
                data-prof-step
                data-step-key={stepKey}
                className="group relative overflow-hidden border-black/10 bg-white shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                onMouseEnter={() => { 
                  setCursorIcon(stepKey); 
                  showCursor(); 
                }}
                onMouseLeave={hideCursor}
              >
                {/* Background gradient */}
                <div 
                  className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: "radial-gradient(120px circle at 20% 0%, rgba(228,22,26,0.08) 0%, transparent 60%), radial-gradient(120px circle at 100% 100%, rgba(18,37,67,0.06) 0%, transparent 60%)",
                  }}
                />

                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Icon Circle */}
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-lg bg-[var(--brand-red)] text-white shadow-md transition-transform group-hover:scale-110">
                      <Icon className="h-5 w-5" />
                      {/* Halo effect */}
                      <span className="absolute inset-0 rounded-lg ring-2 ring-[var(--brand-red)]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>

                    <div className="min-w-0 flex-1">
                      {/* Step Number */}
                      <div className="inline-flex items-center gap-2">
                        <span className="text-[10px] font-extrabold tracking-wide text-neutral-900 bg-black/5 rounded px-2 py-1">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="text-xs text-neutral-500 font-medium">Schritt</span>
                      </div>
                      
                      {/* Title */}
                      <h3 className="mt-2 text-lg font-semibold leading-tight text-neutral-900 group-hover:text-[var(--brand-red)] transition-colors">
                        {step.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="mt-1 text-sm text-neutral-600 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>

                  {/* Optional CTA */}
                  {step.cta && (
                    <div className="mt-4 text-[11px] text-neutral-500 font-medium">
                      {step.cta}
                    </div>
                  )}
                </CardContent>

                {/* Hover underline animation */}
                <div className="absolute left-0 bottom-0 h-[3px] w-0 bg-[var(--brand-red)] group-hover:w-full transition-[width] duration-300 ease-out" />
              </Card>
            );
          })}
        </div>

        {/* Cursor Follower (Desktop only) */}
        <div 
          ref={cursorRef} 
          className="hidden lg:block pointer-events-none absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 opacity-0 scale-90 z-30"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm border border-black/10 shadow-lg text-[var(--brand-red)]">
            {CURSOR_ICON_MAP[cursorIcon] || <Phone className="w-4 h-4" />}
          </div>
        </div>
      </div>
    </section>
  );
}